import * as React from 'react';
import { GraphqlQuery } from 'requestapijs';
import { Loading, Table, Input, Card, Row, Col, Button, Page } from 'sha-el-design';
import { Colums } from 'sha-el-design/lib/components/Table/Table';
import { Link } from 'react-router-dom';
import { questionListQuery } from './constant';
import { FaEdit } from 'react-icons/fa';
import { QuestionNodeEdge, QuestionNodeConnection } from '../../../../schema';
import { Pagination } from '../../Common/Pagination';
import { MdArrowBack, MdHome } from 'react-icons/md';
import '../../Common/listPageStyles';

export class QuestionList extends React.Component<{}, State> {
  constructor(props) {
    super(props);

    this.state = {
      after: '',
      name: '',
      search: '',
    };
  }

  // Debounced: the old input refired the query on every keystroke. `name` is
  // what the input shows; `search` is what the query sees, 300ms later.
  private searchTimer: number = null;

  onSearch = (name: string) => {
    this.setState({ name });
    window.clearTimeout(this.searchTimer);
    // Also reset `after`: searching while standing on page 2+ used to keep the
    // old cursor, so the filtered list started mid-way (or came back empty).
    this.searchTimer = window.setTimeout(
      () => this.setState({ search: name, after: '' }),
      300,
    );
  }

  componentWillUnmount() {
    window.clearTimeout(this.searchTimer);
  }

  typeBadge = (questionType: string) => {
    const label = (questionType || '').split('_').join(' ');
    const variant = label.startsWith('MULTIPLE') ? 'multiple'
      : label.startsWith('SINGLE') ? 'single' : 'text';
    return <span className={`cs-q__type cs-q__type--${variant}`}>{label}</span>;
  }

  columns = (): Array<Colums<QuestionNodeEdge>> => [{
    key: 'type',
    header: 'Type',
    render: (text, obj) => this.typeBadge(obj.node.questionType),
  }, {
    key: 'question',
    header: 'Question',
    render: (text, obj) => (
      <div>
        <Link className='cs-q__name' to={`/dashboard/question/${obj.node.id}`}>
          {obj.node.name}
        </Link>
        <p className='cs-q__desc'>{obj.node.description}</p>
      </div>
    ),
  }, {
    key: 'options',
    header: 'Options',
    render: (text, obj) => {
      const label = (obj.node.questionType || '').split('_').join(' ');
      if (label === 'TEXT' || !(obj.node.options || []).length) {
        return <span className='cs-q__desc'>Free text</span>;
      }
      return (
        <span className='cs-wiz__chips' style={{ justifyContent: 'flex-start' }}>
          {obj.node.options.map((o, i) => (
            <span className='cs-wiz__chip' key={i}>{o}</span>
          ))}
        </span>
      );
    },
  }, {
    key: 'status',
    header: 'Status',
    render: (text, obj) => (
      obj.node.usedInQuestionnaire
        ? <span className='cs-q__status cs-q__status--locked'>In use</span>
        : <span className='cs-q__status cs-q__status--free'>Editable</span>
    ),
  }, {
    key: 'edit',
    header: '',
    render: (text, obj) => (
      <Link
        className='cs-list__call'
        to={`/dashboard/question/${obj.node.id}`}
        title={obj.node.usedInQuestionnaire ? 'View (read-only)' : 'Edit'}
      >
        <FaEdit />
      </Link>
    ),
  }]

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Question', link: '/dashboard/question' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  render() {
    return (
      <div className='cs-list'>
        <Page
          title={'Question List'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard'}>
              <MdArrowBack />
            </Link>
          }
          extra={
            <Row>
              <Col offset={12} span={10}>
                <span className='cs-list__action'>
                  <Link to='/dashboard/question/add'>
                    <Button style={{ width: '100%' }} type='primary'>Add Question</Button>
                  </Link>
                </span>
              </Col>
            </Row>
          }
        >
          <Card>
            {[
              <div className='cs-list__filters' key='search'>
                <div className='cs-list__filter'>
                  <Input
                    placeholder='Search by name'
                    value={this.state.name}
                    onChange={(e) => this.onSearch(e.target.value)}
                  />
                </div>
              </div>,
              <GraphqlQuery
                key='query'
                queryString={questionListQuery}
                variables={{ after: this.state.after, name: this.state.search }}
                render={(response: { allQuestions: QuestionNodeConnection }, error, loading) => {
                  if (error) {
                    return <h3>Something went wrong</h3>;
                  }
                  return (
                    <Loading
                      isLoading={loading}
                      render={() => (
                        <div>
                          {response.allQuestions.edges.length ? (
                            <Table
                              data={response.allQuestions.edges}
                              columns={this.columns()}
                            />
                          ) : (
                            <div className='cs-list__empty'>
                              <h4>{this.state.search ? 'No matching questions' : 'No questions yet'}</h4>
                              <p>
                                {this.state.search
                                  ? `Nothing matches "${this.state.search}".`
                                  : 'Use Add Question to create the first one.'}
                              </p>
                            </div>
                          )}
                          {/* key: a new search resets the pager's internal page
                              counter and cursor stack along with `after`. */}
                          <Pagination
                            key={this.state.search}
                            endCursor={response.allQuestions.pageInfo.endCursor}
                            hasNextPage={response.allQuestions.pageInfo.hasNextPage}
                            after={(after) => this.setState({ after })}
                          />
                        </div>
                      )}
                    />
                  );
                }}
              />,
            ]}
          </Card>
        </Page>
      </div>
    );
  }
}

interface State {
  after: string;
  name: string;
  search: string;
}
